import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Loader2, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import SeoTab from "../../components/seo/SeoTab";
import api, { formatApiError } from "../../lib/api";

const EMPTY = { name: "", slug: "", description: "", logo: "", is_active: true, seo: { auto: true } };

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/brands").then(({ data }) => setBrands(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...EMPTY, ...b, description: b.description || "", logo: b.logo || "" }); setEditingId(b.brand_id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Brand name is required"); return; }
    setSaving(true);
    try {
      if (editingId) await api.patch(`/admin/brands/${editingId}`, form);
      else await api.post("/admin/brands", form);
      toast.success(editingId ? "Brand updated" : "Brand created");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/brands/${b.brand_id}`);
      toast.success("Brand deleted");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not delete brand"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-brands">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-cinzel text-2xl sm:text-3xl text-white tracking-wide">Brands</h1>
          <p className="mt-2 text-muted-foreground">Manage the fireworks brands available when adding or editing products.</p>
        </div>
        <Button data-testid="add-brand-btn" onClick={openNew} className="cf-gold-btn font-semibold gap-2"><Plus className="h-4 w-4" /> Add Brand</Button>
      </div>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
        ) : brands.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground"><Tag className="h-8 w-8 mx-auto text-gold/60" /><p className="mt-3">No brands yet. Create your first one.</p></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/70">Brand</TableHead>
                <TableHead className="text-white/70">Description</TableHead>
                <TableHead className="text-white/70 text-center">Products</TableHead>
                <TableHead className="text-white/70">Status</TableHead>
                <TableHead className="text-white/70 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {brands.map((b) => (
                <TableRow key={b.brand_id} className="border-white/10" data-testid={`brand-row-${b.slug}`}>
                  <TableCell className="text-white font-medium flex items-center gap-3">
                    {b.logo && <img src={b.logo} alt="" className="h-8 w-8 rounded object-contain bg-black/30" />}
                    {b.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-[280px] truncate">{b.description || "—"}</TableCell>
                  <TableCell className="text-center text-white/80">{b.product_count ?? 0}</TableCell>
                  <TableCell><span className={b.is_active ? "text-emerald-400 text-sm" : "text-firered text-sm"}>{b.is_active ? "Active" : "Inactive"}</span></TableCell>
                  <TableCell className="text-right space-x-1">
                    <button onClick={() => openEdit(b)} title="Edit" data-testid={`edit-brand-${b.slug}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(b)} title="Delete" data-testid={`delete-brand-${b.slug}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-firered hover:bg-firered/10"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </GlassCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="cf-glass border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="brand-dialog">
          <DialogHeader><DialogTitle className="font-cinzel text-xl">{editingId ? "Edit Brand" : "New Brand"}</DialogTitle>
            <DialogDescription className="text-white/50">Manage brand details and SEO. Changes apply to the public brand page.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details" data-testid="brand-tab-details">Details</TabsTrigger>
              <TabsTrigger value="seo" data-testid="brand-tab-seo">SEO</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="space-y-4 mt-4">
              <div><Label className="text-white/80">Name *</Label><Input data-testid="brand-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
              <div><Label className="text-white/80">Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
              <div><Label className="text-white/80">Logo URL (optional)</Label><Input value={form.logo} onChange={(e) => set("logo", e.target.value)} placeholder="https://…" className="mt-1 bg-black/30 border-white/15 text-white" /></div>
              <div className="flex items-center gap-2"><Switch data-testid="brand-active-switch" checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} /><Label className="text-white/80">Active</Label></div>
            </TabsContent>
            <TabsContent value="seo" className="mt-4">
              <SeoTab
                entityType="brand"
                entity={{ brand_id: editingId, name: form.name, description: form.description, logo: form.logo, seo: form.seo }}
                value={form.seo}
                onChange={(seo) => set("seo", seo)}
                slug={form.slug}
                onSlugChange={(v) => set("slug", v)}
              />
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70">Cancel</Button>
            <Button data-testid="save-brand-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
